import React, {createContext, useContext, useEffect, useState} from 'react';
import {
  getRemoteConfig,
  fetchAndActivate,
  getValue,
} from 'firebase/remote-config';
import {auth} from '@src/firebase';

interface FirebaseRemoteConfigContextType {
  anonymousAuthEnabled: boolean;
  isLoading: boolean;
}

const FirebaseRemoteConfigContext =
  createContext<FirebaseRemoteConfigContextType>({
    anonymousAuthEnabled: false,
    isLoading: true,
  });

export const useFirebaseRemoteConfig = () => {
  const context = useContext(FirebaseRemoteConfigContext);
  if (!context) {
    throw new Error(
      'useFirebaseRemoteConfig must be used within FirebaseRemoteConfigProvider',
    );
  }
  return context;
};

interface FirebaseRemoteConfigProviderProps {
  children: React.ReactNode;
}

const FirebaseRemoteConfigProvider: React.FC<
  FirebaseRemoteConfigProviderProps
> = ({children}) => {
  const [anonymousAuthEnabled, setAnonymousAuthEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initRemoteConfig = async () => {
      try {
        const remoteConfig = getRemoteConfig(auth.app);

        // Configuration par défaut
        remoteConfig.defaultConfig = {
          anonymous_auth: false,
        };

        // Paramètres de fetch
        remoteConfig.settings = {
          minimumFetchIntervalMillis: 300000, // 5 minutes
          fetchTimeoutMillis: 60000, // 1 minute
        };

        // Fetch et activation des nouvelles valeurs
        await fetchAndActivate(remoteConfig);

        // Récupération de la valeur
        const value = getValue(remoteConfig, 'anonymous_auth');
        setAnonymousAuthEnabled(value.asBoolean());

        console.log(
          '✅ Firebase Remote Config - anonymous_auth:',
          value.asBoolean(),
        );
      } catch (error) {
        console.error(
          "❌ Erreur lors de l'initialisation de Firebase Remote Config:",
          error,
        );
        // Utiliser la valeur par défaut en cas d'erreur
        setAnonymousAuthEnabled(false);
      } finally {
        setIsLoading(false);
      }
    };

    initRemoteConfig();
  }, []);

  return (
    <FirebaseRemoteConfigContext.Provider
      value={{anonymousAuthEnabled, isLoading}}>
      {children}
    </FirebaseRemoteConfigContext.Provider>
  );
};

export default FirebaseRemoteConfigProvider;
